import { ExportParams, ExportType, exportDataThunk } from 'actions/dataExport/DataExportActions';
import Button from 'components/UI/V3/Button/Button';
import { RadioGroup, RadioOption } from 'components/UI/V3/Forms/RadioGroup/RadioGroup';
import moment, { Moment } from 'moment';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import styles from './QuickbooksForm.module.scss';
import { DatePickerDropdown } from './DatePickerDropdown';
import { showWarning } from '@spike/notifications-action';

/**
 * QuickbooksForm component containing export type selector, date range pickers, and export button
 */
const QuickbooksForm: FunctionComponent = () => {
    const dispatch = useDispatch();

    const { loading } = useSelector((state: RootState) => state.dataExport);

    // Date state
    const [startDate, setStartDate] = useState<Moment | null>(moment().subtract(30, 'days'));
    const [endDate, setEndDate] = useState<Moment | null>(moment());
    const [dateError, setDateError] = useState('');

    // Form state
    const [exportType, setExportType] = useState<ExportType>('customers');

    // Validate date range whenever dates change
    useEffect(() => {
        if (startDate && endDate) {
            if (startDate.isAfter(endDate)) {
                setDateError('Start date must be before or equal to end date');
            } else {
                setDateError('');

                // Check if date range is greater than 31 days
                const daysDifference = endDate.diff(startDate, 'days');
                if (daysDifference > 31) {
                    dispatch(showWarning('Large exports may take longer. Consider smaller ranges.'));
                }
            }
        }
    }, [startDate, endDate, dispatch]);

    /**
     * Handle export button click
     */
    const handleExport = () => {
        // Validate dates before exporting
        if (dateError) {
            return;
        }

        if (!startDate || !endDate) {
            setDateError('Both start and end dates are required');
            return;
        }

        const params: ExportParams = {
            type: exportType,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
        };

        dispatch(exportDataThunk(params));
    };

    // Radio options for export type
    const exportTypeOptions: RadioOption<ExportType>[] = [
        { value: 'customers', inlineLabel: 'Clients' },
        { value: 'invoices', inlineLabel: 'Invoices' },
        { value: 'payments', inlineLabel: 'Payments' },
        { value: 'products_services', inlineLabel: 'Products and Services' },
        { value: 'bundle', inlineLabel: 'Bundle (All Data)' }
    ];

    return (
        <div className={styles.formContainer}>
            {/* Date Range Selection */}
            <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>Date Range</h3>
                <div className={styles.dateRangeRow}>
                    <div className={styles.dateField}>
                        <DatePickerDropdown
                            value={startDate}
                            onChange={setStartDate}
                            label="FROM"
                            placeholder="MMM DD, YYYY"
                            disabled={loading}
                        />
                    </div>
                    <div className={styles.dateField}>
                        <DatePickerDropdown
                            value={endDate}
                            onChange={setEndDate}
                            label="TO"
                            placeholder="MMM DD, YYYY"
                            disabled={loading}
                        />
                    </div>
                </div>
                {dateError && <div className={styles.errorText}>{dateError}</div>}

                {/* Export Type Selection */}
                <div className={styles.formSection}>
                    <h3 className={styles.sectionTitle}>Select Data To Export</h3>
                    <RadioGroup
                        value={exportType}
                        options={exportTypeOptions}
                        onChange={setExportType}
                        className={styles.radioGroup}
                    />
                </div>
            </div>

            {/* Export Button */}
            <div className={styles.buttonRow}>
                <Button
                    className={styles.exportButton}
                    label={loading ? 'Exporting...' : 'Export'}
                    onClick={handleExport}
                    disabled={loading || !!dateError}
                    isLoading={loading}
                />
            </div>
        </div>
    );
};

export default QuickbooksForm;
